import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { splitSpecialDateTime, combineSpecialDateTime, validateSpecialDateRange } from './specialTime';
import {
  ArrowRight,
  Beef,
  CakeSlice,
  Flame,
  Camera,
  ChevronDown,
  ChevronUp,
  Clock,
  Coffee,
  GripVertical,
  MapPin,
  Menu,
  MoreVertical,
  Navigation,
  Phone,
  Plus,
  Save,
  Search,
  ShieldCheck,
  ShoppingBasket,
  ShowerHead,
  Scale,
  Trash2,
  Truck,
  Toilet,
  UserPlus,
  Utensils,
  WashingMachine,
  Wifi
} from 'lucide-react';
import type { FuelGrade, FuelHistoryRecord, FuelPrice, ManagedUser, MenuCategory, MenuItem, Permission, PublicSettings, ReleaseRecord, SessionUser, Special, SupportInfo } from '@heartland/shared';
import { allPermissions, hasPermission, roleLabel } from '@heartland/shared';
import { api, sessionExpiredEvent } from './api';
import { formatFuelAmount, formatFuelPrice } from './fuelDisplay';
import { actorLabel, customerText } from './customerPresentation';
import { StagingNotice } from './StagingNotice';
import type { FuelSyncStatus } from './api';
import './styles.css';
import { FoodGallery } from './FoodGallery';
import { DisplayOptions } from './DisplayOptions';

const address = '4155 S Westwood Blvd, Poplar Bluff, MO 63901';
const phone = '573-778-0772';
const facebookUrl = 'https://www.facebook.com/HeartlandExpressPB/';
const mapsQuery = encodeURIComponent(address);
const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
const appleMapsUrl = `https://maps.apple.com/?q=${mapsQuery}`;

const defaultSettings: PublicSettings = {
  orderOnline: { label: 'Order Online', url: null, enabled: false, needsConfirmation: true },
  foodHoursConfirmation: []
};

function Header() {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const breakpoint = window.matchMedia('(max-width: 1000px)');
    const reset = () => setOpen(false);
    breakpoint.addEventListener('change', reset);
    return () => breakpoint.removeEventListener('change', reset);
  }, []);
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('dialog[open]')) return;
      event.preventDefault();
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open]);
  const nav = [
    { label: 'Home', href: '/', mobileOnly: true },
    { label: 'Food & Dining', href: '/menu' },
    { label: '57 Diner', href: '/#57-diner' },
    { label: 'Market', href: '/#market' },
    { label: 'Truckers', href: '/#trucker-amenities' },
    { label: 'About', href: '/#about' },
    { label: 'Contact', href: '/#directions' }
  ];
  return (
    <>
      <div className="utility-bar">
        <span>Good food. Good company. Trucker amenities.</span>
        <span><MapPin size={14} /> {address}</span>
        <span><Phone size={14} /> {phone}</span>
      </div>
      <header className={`site-header${open ? ' navigation-expanded' : ''}`}>
        <a className="brand logo-lockup" href="/" aria-label="Heartland Express home">
          <span className="logo-heartland">Heartland<br /><b>Express</b></span>
        </a>
        <button ref={toggleRef} type="button" className="icon-button mobile-only navigation-toggle" onClick={() => setOpen((value) => !value)} aria-label={open ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={open} aria-controls="site-navigation">
          {open ? <span className="navigation-close" aria-hidden="true">×</span> : <Menu size={22} aria-hidden="true" />}
          <span>{open ? 'Close' : 'Menu'}</span>
        </button>
        <nav id="site-navigation" aria-label="Main navigation" onClick={(event) => { if ((event.target as HTMLElement).closest('a')) setOpen(false); }} className={open ? 'open' : ''}>
          {nav.map((item) => <a className={item.mobileOnly ? 'mobile-nav-only' : ''} key={item.label} href={item.href} aria-current={!item.href.includes('#') && window.location.pathname === item.href ? 'page' : undefined}>{item.label}</a>)}
        </nav>
        <div className="header-actions">
          <OrderButton />
          <a className="action ghost outlined" href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={18} /> Get Directions</a>
        </div>
        <DisplayOptions />
      </header>
    </>
  );
}


function HeroImage() {
  return <div className="hero-media"><img src="/images/heartland-meatloaf-full-plate.webp" alt="" /></div>;
}

function HomePhoto({ tone, label }: { tone: 'food' | 'diner' | 'market' | 'fuel'; label: string }) {
  const photos = {
    food: '/images/heartland-food-hero.webp',
    diner: '/images/heartland-diner-food.webp',
    market: '/images/heartland-market-drinks.webp',
    fuel: '/images/heartland-express-hero-1280.webp'
  };
  return <img className={`home-photo ${tone}`} src={photos[tone]} alt={label} loading="lazy" />;
}

function OrderButton({ compact = false }: { compact?: boolean }) {
  const [settings, setSettings] = useState<PublicSettings>(defaultSettings);
  useEffect(() => { void api.publicSettings().then(setSettings).catch(() => undefined); }, []);
  const label = compact ? 'Order' : settings.orderOnline.label || 'Order Online';
  const content = <><Utensils size={compact ? 20 : 18} /> {label}</>;
  if (settings.orderOnline.enabled && settings.orderOnline.url) {
    return <a className={compact ? undefined : 'action primary'} href={settings.orderOnline.url} rel="noopener noreferrer" target="_blank">{content}</a>;
  }
  return <button className={compact ? undefined : 'action primary'} type="button" disabled title="Online ordering is not available yet">{content}</button>;
}

function PublicSite() {
  const [menu, setMenu] = useState<{ categories: MenuCategory[]; items: MenuItem[] }>({ categories: [], items: [] });
  const [specials, setSpecials] = useState<Special[]>([]);
  const [fuelPrices, setFuelPrices] = useState<FuelPrice[]>([]);
  const [fuelStatus, setFuelStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    void api.publicMenu().then(setMenu).catch(() => undefined);
    void api.publicSpecials().then((data) => setSpecials(data.specials)).catch(() => undefined);
    void api.publicFuelPrices().then((data) => { setFuelPrices(data.prices); setFuelStatus('ready'); }).catch(() => setFuelStatus('error'));
  }, []);

  const featuredItems = menu.items.filter((item) => item.featured).slice(0, 4);
  const visibleItems = (featuredItems.length ? featuredItems : menu.items).slice(0, 6);
  const featuredSpecial = specials.find((special) => special.featured) ?? specials[0];
  const tiles = [
    { id: 'food-dining-tile', icon: Utensils, title: 'Food & Dining', body: 'Fresh favorites, quick bites, and diner staples for the road.', cta: 'View Menu', tone: 'food' as const, href: '/menu' },
    { id: '57-diner-tile', icon: Coffee, title: '57 Diner', body: 'Classic comfort food with a Heartland welcome.', cta: 'Visit the Diner', tone: 'diner' as const, href: '#57-diner' },
    { id: 'market-tile', icon: ShoppingBasket, title: 'Market', body: 'Snacks, drinks, coffee, and travel basics for the next stretch.', cta: 'Explore Market', tone: 'market' as const, href: '#market' },
    { id: 'trucker-amenities-tile', icon: Truck, title: 'Truckers Welcome', body: 'Fuel, parking, showers and a comfortable break.', cta: 'Our Amenities', tone: 'fuel' as const, href: '#trucker-amenities' }
  ];
  const truckerAmenities = [
    { title: 'CAT Scale', body: 'Certified truck scale access for drivers who need a reliable weigh-in.', icon: Scale, featured: true, logo: '/images/cat-scale-logo-official.png' },
    { title: 'Overnight Parking', body: 'Contact the store for parking details and availability.', icon: Truck },
    { title: 'Showers', body: 'Clean up and refresh during a long haul.', icon: ShowerHead },
    { title: 'Laundry', body: 'On-site laundry support for drivers on the road.', icon: WashingMachine },
    { title: 'High-Speed Wi-Fi', body: 'Stay connected while you stop.', icon: Wifi },
    { title: 'Restrooms', body: 'Clean restroom access for travelers and drivers.', icon: Toilet },
    { title: 'Food / 57 Diner', body: 'Hot food and diner favorites when it is time to eat.', icon: Utensils },
    { title: 'Bakery', body: '', icon: CakeSlice },
    { title: 'BBQ', body: '', icon: Flame },
    { title: 'Meat Department', body: '', icon: Beef }
  ];

  return (
    <>
      <Header />
      <main className="home-page">
        <section id="fuel-prices" className="fuel-strip" aria-label="Current fuel prices">
          <FuelPriceSign prices={fuelPrices} status={fuelStatus} />
        </section>
        <section className="hero hero-with-gallery">
          <div className="hero-copy">
            <h1>More Than<br />a Truck <span>Stop.</span></h1>
            <p>Great food. A full market. Trucker amenities. And now, the 57 Diner.</p>
            <div className="hero-actions">
              <OrderButton />
              <a className="action dark translucent" href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={18} /> Get Directions</a>
            </div>
          </div>
          <HeroImage />
          <FoodGallery />
        </section>

        <section className="tile-strip" aria-label="Heartland quick links">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <article className="home-tile" id={tile.id} key={tile.id}>
                <HomePhoto label={tile.tone === 'market' ? 'Cold Coca-Cola from the Heartland drinks selection' : tile.tone === 'fuel' ? 'Heartland Express exterior' : 'Food from the Heartland menu'} tone={tile.tone} />
                <div className="tile-body">
                  <span className="round-icon" aria-hidden="true"><Icon size={24} /></span>
                  <h2>{tile.title}</h2>
                  <p>{tile.body}</p>
                  <a className="tile-link" aria-label={`${tile.title}: ${tile.cta}`} href={tile.href ?? `#${tile.id}`}>{tile.cta} <ArrowRight size={14} /></a>
                </div>
              </article>
            );
          })}
        </section>

        <section id="specials" className="specials-band" aria-label="Today's specials">
          <div className="specials-heading"><h2>Today's Specials</h2><p>Good food. A welcome break.</p><a className="action primary" href="/menu">View Full Menu <ArrowRight size={16} /></a></div>
          <FoodSpecialPanel special={featuredSpecial} />
        </section>

        <section id="food-dining" className="band menu-band">
          <div className="section-heading">
            <div><p className="eyebrow">Food & Dining</p><h2>Favorites From Heartland</h2><p>A quick look at popular picks from the Heartland menu.</p></div>
            <a className="action primary" href="/menu"><Utensils size={18} /> View Full Menu</a>
          </div>
          <div className="cards menu-grid">
            {visibleItems.map((item) => <MenuCard item={item} key={item.id} />)}
          </div>
        </section>

        <section id="57-diner" className="band split diner-band">
          <HomePhoto label="Chicken dumplings from the Heartland menu" tone="diner" />
          <div>
            <p className="eyebrow">57 Diner</p>
            <h2>Classic Comfort Food, Built Into the Stop</h2>
            <p>Pull in for a real meal, a warm cup of coffee, and a comfortable break before the next stretch.</p>
            <a className="action primary" href="/menu"><Utensils size={18} /> View Diner Favorites</a>
          </div>
        </section>

        <section id="market" className="band split market">
          <HomePhoto label="Cold Coca-Cola from the Heartland drinks selection" tone="market" />
          <div><p className="eyebrow">Market</p><h2>Everything for the Next Stretch</h2><p>Grab snacks, drinks, coffee, and road-trip basics without losing time off route.</p>
            <ul className="market-departments" aria-label="Heartland departments">
              <li>Bakery Department</li><li>BBQ</li><li>Meat Department</li>
            </ul>
          </div>
        </section>

        <section id="bakery-bbq-meats" className="band department-showcase" aria-labelledby="department-showcase-title">
          <div className="department-showcase-heading">
            <p className="eyebrow">Our Departments</p>
            <h2 id="department-showcase-title">Bakery, BBQ &amp; Fresh Meats</h2>
            <p>Department photos and details are coming soon.</p>
          </div>
          <div className="department-showcase-grid">
            {[
              { title: 'Bakery', image: '/images/department-bakery-sample.webp', description: 'pastries and cinnamon rolls' },
              { title: 'BBQ', image: '/images/department-bbq-sample.webp', description: 'smoked brisket and barbecue ribs' },
              { title: 'Meat Department', image: '/images/department-meat-sample.webp', description: 'fresh steaks and pork chops' }
            ].map(({ title, image, description }) => (
              <article className="department-showcase-card" key={title}>
                <div className="department-sample-photo">
                  <img src={image} alt={`AI-generated illustrative sample of ${description}; not an actual Heartland department photo`} loading="lazy" decoding="async" />
                  <span className="department-sample-label">Sample Image</span>
                </div>
                <h3>{title}</h3>
              </article>
            ))}
          </div>
        </section>

        <section id="trucker-amenities" className="band amenities amenities-band amenities-only">
          <div className="amenities-feature">
            <div className="section-heading amenities-heading">
              <div>
                <p className="eyebrow">Trucker Amenities</p>
                <h2>Built for Professional Drivers</h2>
                <p>Stop, reset, and get back on the road with the services drivers need most.</p>
              </div>
            </div>
            <div className="amenity-grid">
              {truckerAmenities.map((amenity) => {
                const Icon = amenity.icon;
                return (
                  <article className={amenity.featured ? 'amenity-card featured cat-scale-card' : 'amenity-card'} key={amenity.title}>
                    <span>{amenity.logo ? <img src={amenity.logo} alt="CAT Scale" /> : <Icon size={24} />}</span>
                    <div><h3>{amenity.title}</h3>{amenity.body && <p>{amenity.body}</p>}</div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="about" className="band about-band">
          <h2>Who We Are</h2>
          <div className="about-story">
            <p>{"25 years ago, Heartland was just another stop for fuel, drinks, and snacks. Today, we’re a regional destination for working folks, families, travelers, and the truck drivers who keep America moving. Good food brings people through our doors. How we treat them brings them back."}</p>
            <p>{"We’re a foodie joint with a Southern soul: Generous portions of freshly prepared, homestyle food. Meatloaf worth the drive. American catfish from the Mississippi Delta. Award-winning Ole’ Hickory barbecue. And a Southern breakfast spread that means business."}</p>
            <p>{"We’re proud to be family owned and operated, with a bright, welcoming facility, Phillips 66 fuel, and doors open 24/7. Friendly service and warm Southern hospitality come with the territory."}</p>
            <p>{"You’ll find us at the M Highway exit off the future Interstate 57 in Poplar Bluff, Missouri—roughly three hours from St. Louis, Memphis, and Little Rock."}</p>
            <p>{"But our place on the map tells only part of our story. Heartland is where familiar faces become friends, and friends become family. Where knowing your name means knowing a little of your story, too. Your children. Your milestones. The good days worth celebrating and the hard days when a little kindness matters most."}</p>
            <div className="about-actions">
              <a className="action primary about-call-button" href={`tel:${phone.replaceAll('-', '')}`}><Phone size={18} aria-hidden="true" /> Call {phone}</a>
              <a className="action facebook-brand-button" href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Heartland Express on Facebook (opens in a new tab)"><img className="facebook-official-logo" src="/images/facebook-logo-secondary-official.png" alt="" /> <span>Visit our page</span></a>
            </div>
          </div>
          <aside className="about-visual" aria-label="The Heartland Way">
            <HomePhoto label="Heartland Express and Phillips 66 fuel canopy" tone="fuel" />
            <div className="about-family-callout">
              <p className="about-family-quote">We do family.<br />The Heartland Way.</p>
              <p className="about-family-invitation">Come be part of it.</p>
            </div>
          </aside>
        </section>

        <section id="google-reviews" className="band reviews-band">
          <div><p className="eyebrow">Guest Experiences</p><h2>Find Us on Google</h2>
            <p>Read what guests share about Heartland Truck Stop, or tell us about your visit.</p>
          </div>
          <div className="review-actions">
            <a className="action ghost outlined" href="https://www.google.com/maps/place/HEARTLAND+TRUCK+STOP/@36.7288692,-90.4596773,17z/data=!4m8!3m7!1s0x87d7ae2fde3aaead:0x57a71ba066b352ba!8m2!3d36.7288692!4d-90.4596773!9m1!1b1!16s%2Fg%2F1tptr4cn" target="_blank" rel="noopener noreferrer">Read Google Reviews <ArrowRight size={16} /></a>
            <a className="action primary" href="https://search.google.com/local/writereview?placeid=ChIJra463i-u14cRulKzZqAbp1c" target="_blank" rel="noopener noreferrer">Write a Google Review <ArrowRight size={16} /></a>
            <small>Opens Google in a new tab. A Google Account is needed to write a review.</small>
          </div>
        </section>

        <section id="heartland-family" className="band family-band">
          <p className="eyebrow">The Heartland Family</p>
          <h2>Meet the People Behind the Stop</h2>
          <p>Team photos and introductions are being prepared.</p>
          <p className="content-pending">Preview note: staff photographs and biographies will be added when supplied.</p>
        </section>

        <section id="directions" className="band directions">
          <div className="direction-copy">
            <p className="eyebrow">Contact / Directions</p>
            <h2>Easy In, Easy Out</h2>
            <div className="contact-details">
              <a className="contact-line" href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={20} /> <span>{address}</span></a>
              <a className="contact-line" href={`tel:${phone.replaceAll('-', '')}`}><Phone size={20} /> <span>{phone}</span></a>
              <span className="contact-line"><Clock size={20} /> <span>Open 24/7</span></span>
            </div>
            <div className="direction-buttons">
              <a className="action primary" href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><Navigation size={18} /> Google Maps</a>
              <a className="action ghost outlined" href={appleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={18} /> Apple Maps</a>
              <a className="action dark" href={`tel:${phone.replaceAll('-', '')}`}><Phone size={18} /> Call</a>
            </div>
          </div>
          <div className="map-preview" aria-label={`Map preview centered on Heartland Express at ${address}`}>
            <div className="map-road north-south" />
            <div className="map-road east-west" />
            <div className="map-road feeder" />
            <div className="map-card">
              <span className="map-pin"><MapPin size={22} /></span>
              <strong>Heartland Express</strong>
              <small>{address}</small>
            </div>
          </div>
        </section>
      </main>
      <div className="mobile-action-bar">
        <OrderButton compact />
        <a href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={20} />Directions</a>
        <a href={`tel:${phone.replaceAll('-', '')}`}><Phone size={20} />Call</a>
      </div>
      <footer className="site-footer">
        <div className="footer-main">
          <div className="footer-identity">
            <span className="logo-heartland footer-logo">Heartland<br /><b>Express</b></span>
            <p className="footer-tagline">Good Food. Further Together.</p>
          </div>
          <div className="footer-details">
            <span className="footer-label">Visit Heartland</span>
            <span>{address}</span>
            <a className="footer-phone" href={`tel:${phone.replaceAll('-', '')}`}>{phone}</a>
          </div>
          <div className="footer-social">
            <span className="footer-label">Find us on Facebook</span>
            <a className="action facebook-brand-button" href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Heartland Express on Facebook (opens in a new tab)"><img className="facebook-official-logo" src="/images/facebook-logo-secondary-official.png" alt="" /> <span>Visit our page</span></a>
          </div>
        </div>
      </footer>
    </>
  );
}

function MenuPage() {
  const [menu, setMenu] = useState<{ categories: MenuCategory[]; items: MenuItem[] }>({ categories: [], items: [] });
  useEffect(() => { void api.publicMenu().then(setMenu).catch(() => undefined); }, []);
  const categories = menu.categories.filter((category) => menu.items.some((item) => item.categoryId === category.id));
  return (
    <>
      <Header />
      <main>
        <section className="menu-page-hero">
          <div>
            <p className="eyebrow">Heartland Menu</p>
            <h1>Food & Dining</h1>
            <p>Browse the current Heartland menu. Order Online opens in a new tab when online ordering is available.</p>
          </div>
          <OrderButton />
        </section>
        <section className="band menu-band full-menu">
          <div className="category-pills">{categories.map((category) => <a href={`#category-${category.id}`} key={category.id}>{category.name}</a>)}</div>
          {categories.map((category) => (
            <div className="menu-category-section" id={`category-${category.id}`} key={category.id}>
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Menu</p>
                  <h2>{category.name}</h2>
                  {category.description && <p>{category.description}</p>}
                </div>
              </div>
              <div className="cards menu-grid">
                {menu.items.filter((item) => item.categoryId === category.id).map((item) => <MenuCard item={item} key={item.id} />)}
              </div>
            </div>
          ))}
        </section>
      </main>
      <div className="mobile-action-bar">
        <OrderButton compact />
        <a href={googleMapsUrl} rel="noopener noreferrer" target="_blank"><MapPin size={20} />Directions</a>
        <a href={`tel:${phone.replaceAll('-', '')}`}><Phone size={20} />Call</a>
      </div>
      <footer className="site-footer">
        <div className="footer-main">
          <div className="footer-identity">
            <span className="logo-heartland footer-logo">Heartland<br /><b>Express</b></span>
            <p className="footer-tagline">Good Food. Further Together.</p>
          </div>
          <div className="footer-details">
            <span className="footer-label">Visit Heartland</span>
            <span>{address}</span>
            <a className="footer-phone" href={`tel:${phone.replaceAll('-', '')}`}>{phone}</a>
          </div>
          <div className="footer-social">
            <span className="footer-label">Find us on Facebook</span>
            <a className="action facebook-brand-button" href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Heartland Express on Facebook (opens in a new tab)"><img className="facebook-official-logo" src="/images/facebook-logo-secondary-official.png" alt="" /> <span>Visit our page</span></a>
          </div>
        </div>
      </footer>
    </>
  );
}

function MenuCard({ item }: { item: MenuItem }) {
  return (
    <article className="menu-card">
      {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <figure className="menu-photo-fallback"><img src="/images/heartland-express-hero-768.webp" alt="Heartland Express location" loading="lazy" /><figcaption>Item photo coming soon</figcaption></figure>}
      <div>
        <h3>{item.name}</h3>
        <p>{item.description}</p>
        <strong>${item.price}</strong>
      </div>
    </article>
  );
}

function FuelPriceSign({ prices, status = 'ready' }: { prices: FuelPrice[]; status?: 'loading' | 'ready' | 'error' }) {
  const byGrade = Object.fromEntries(prices.map((price) => [price.grade, price]));
  const primary = [byGrade.regular, byGrade.diesel].filter((price) => price?.enabled) as FuelPrice[];
  const secondary = [byGrade.midgrade, byGrade.premium].filter((price) => price?.enabled && price.price) as FuelPrice[];
  const lastUpdated = prices.map((price) => price.lastUpdatedAt).filter(Boolean).sort().at(-1);
  return (
    <article className="fuel-sign">
      <div className="fuel-brand">
        <img className="phillips-reference-mark" src="/images/phillips-66-logo-official.png" alt="Phillips 66" />
        <div>
          <strong>Heartland Express</strong>
          <span className="fuel-brand-caption">Current fuel prices</span>
        </div>
      </div>
      <div className="fuel-primary">
        {status !== 'ready' || prices.length === 0 ? <span className="fuel-status" role="status">{status === 'loading' ? 'Loading fuel prices…' : status === 'error' ? 'Prices unavailable. Please call for current prices.' : 'Please call for current prices.'}</span> : null}
        {primary.map((price) => <FuelGradeRow key={price.grade} price={price} />)}
      </div>
      {secondary.length > 0 && <div className="fuel-secondary">{secondary.map((price) => <FuelGradeRow key={price.grade} price={price} small />)}</div>}
      <p>Last updated {lastUpdated ? <time dateTime={lastUpdated}>{new Date(lastUpdated).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</time> : 'pending manager update'}.</p>
      <small>Credit prices. Prices subject to change.</small>
    </article>
  );
}

function FuelGradeRow({ price, small = false }: { price: FuelPrice; small?: boolean }) {
  const formatted = formatFuelAmount(price.price);
  const [dollars, cents] = formatted ? formatted.split('.') : ['--', ''];
  return (
    <div className={small ? 'fuel-row small' : 'fuel-row'} data-fuel-grade={price.grade}>
      <span>{price.label}</span>
      <strong className="fuel-price">
        {formatted ? <><small>$</small><span className="fuel-digits">{dollars}<span className="fuel-decimal">.</span>{cents}</span></> : '--'}
      </strong>
    </div>
  );
}

function SpecialMealPhoto({ special }: { special?: Special }) {
  const [failed, setFailed] = useState(false);
  if (!special?.photoUrl || failed) {
    return <div className="special-meal-photo special-meal-placeholder"><Camera size={24} aria-hidden="true" /><span>Meal photo coming soon</span></div>;
  }
  return <img className="special-meal-photo" src={special.photoUrl} alt={`${special.name} meal`} loading="lazy" onError={() => setFailed(true)} />;
}

function FoodSpecialPanel({ special }: { special?: Special }) {
  return (
    <article className="food-now">
      <SpecialMealPhoto key={special?.photoUrl ?? 'no-photo'} special={special} />
      <p className="eyebrow">Featured Special</p>
      <h2>{special?.name ?? 'Daily Diner Special'}</h2>
      <p>{special?.description ?? 'Ask us about today’s diner specials.'}</p>
      {special?.price && <strong>${special.price}</strong>}
      <OrderButton />
    </article>
  );
}

function displayRole(role: SessionUser['role']): string {
  return roleLabel(role);
}

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Never';
}

function summarizeChange(row: Record<string, unknown>): string {
  const previous = row.previousValue as Record<string, unknown> | null;
  const next = row.newValue as Record<string, unknown> | null;
  if (row.action === 'update_price' && previous && next) return `${previous.price ?? 'n/a'} -> ${next.price ?? 'n/a'}`;
  if (row.action === 'update_fuel_price' && previous && next) return `${formatFuelPrice(previous.price)} -> ${formatFuelPrice(next.price)}`;
  if (row.action === 'reset_password') return 'Temporary password issued';
  if (row.action === 'grant_permission' && next) return `Granted ${next.permission}`;
  if (row.action === 'revoke_permission' && previous) return `Revoked ${previous.permission}`;
  if (row.action === 'upload_image') return 'Photo uploaded';
  if (row.action === 'replace_image') return 'Photo replaced';
  if (row.action === 'remove_image') return 'Photo removed';
  return String(row.action ?? '').replaceAll('_', ' ');
}

function AdminApp() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('menu');
  const [release, setRelease] = useState<ReleaseRecord | null>(null);
  const [releaseStatus, setReleaseStatus] = useState('Version loading');
  useEffect(() => {
    const expired = () => { setUser(null); setRelease(null); setError('Your session expired. Please sign in again.'); };
    window.addEventListener(sessionExpiredEvent, expired);
    return () => window.removeEventListener(sessionExpiredEvent, expired);
  }, []);
  useEffect(() => { void api.me().then(setUser).catch(() => undefined); }, []);
  useEffect(() => {
    if (!user) return;
    if (!hasPermission(user, 'releases.view')) { setReleaseStatus('Heartland Management'); return; }
    setReleaseStatus('Version loading');
    void api.releases().then((data) => { setRelease(data.releases[0] ?? null); setReleaseStatus('No release recorded'); }).catch(() => setReleaseStatus('Version unavailable'));
  }, [user]);
  if (!user) return <Login onLogin={setUser} error={error} setError={setError} />;
  if (user.mustChangePassword) return <PasswordChangeRequired user={user} onChanged={setUser} />;
  const nav = [
    ...(user.role === 'manager' ? [{ key: 'dashboard', label: 'Dashboard', permission: 'menu.view' as Permission }] : []),
    { key: 'menu', label: 'Menu', permission: 'menu.view' as Permission },
    { key: 'fuel', label: 'Fuel', permission: 'fuel.view' as Permission },
    { key: 'specials', label: 'Specials', permission: 'specials.view' as Permission },
    { key: 'users', label: 'Staff & Managers', permission: 'users.view' as Permission },
    { key: 'audit', label: 'Audit', permission: 'audit.view' as Permission },
    { key: 'releases', label: 'Releases', permission: 'releases.view' as Permission },
    { key: 'support', label: 'Support', permission: 'support.view' as Permission }
  ].filter((item) => hasPermission(user, item.permission));
  const active = nav.some((item) => item.key === tab) ? tab : nav[0]?.key ?? 'menu';
  return (
    <main className="admin-shell">
      <aside>
        <a className="brand" href="/manage"><span className="brand-mark">H</span><span><strong>Heartland</strong><small>Management</small></span></a>
        {nav.map((item) => <button key={item.key} className={active === item.key ? 'active' : ''} onClick={() => setTab(item.key)}>{item.label}</button>)}
        <button onClick={() => void api.logout().then(() => setUser(null))}>Log out</button>
        <div className="admin-version">{release ? `${release.version} · ${release.releaseNumber}` : releaseStatus}</div>
      </aside>
      <section className="admin-panel">
        <div className="admin-top"><h1>{nav.find((item) => item.key === active)?.label ?? 'Menu'}</h1><span><ShieldCheck size={16} /> {customerText(user.displayName)} - {displayRole(user.role)}</span></div>
        {active === 'dashboard' && <ManagerDashboard setTab={setTab} />}
        {active === 'menu' && <MenuAdmin user={user} />}
        {active === 'fuel' && <FuelAdmin user={user} />}
        {active === 'specials' && <SpecialsAdmin user={user} />}
        {active === 'users' && <UsersAdmin user={user} />}
        {active === 'audit' && <AuditAdmin />}
        {active === 'releases' && <ReleasesAdmin />}
        {active === 'support' && <SupportAdmin />}
      </section>
    </main>
  );
}

function ManagerDashboard({ setTab }: { setTab: (tab: string) => void }) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [prices, setPrices] = useState<FuelPrice[]>([]);
  const [specials, setSpecials] = useState<Special[]>([]);
  useEffect(() => { void Promise.all([api.items(), api.fuelPrices(), api.specials()]).then(([itemData, fuelData, specialData]) => { setItems(itemData.items); setPrices(fuelData.prices); setSpecials(specialData.specials); }); }, []);
  const regular = prices.find((price) => price.grade === 'regular');
  const diesel = prices.find((price) => price.grade === 'diesel');
  const featured = specials.find((special) => special.active && special.featured);
  return (
    <div className="manager-stack">
      <div className="dashboard-grid">
        <article><span>Regular</span><strong>{formatFuelPrice(regular?.price)}</strong></article>
        <article><span>Diesel</span><strong>{formatFuelPrice(diesel?.price)}</strong></article>
        <article><span>Active special</span><strong>{featured?.name ?? 'None set'}</strong></article>
        <article><span>Active menu items</span><strong>{items.filter((item) => item.active).length}</strong></article>
      </div>
      <div className="quick-actions">
        <button className="action primary" onClick={() => setTab('menu')}>Edit Menu</button>
        <button className="action primary" onClick={() => setTab('fuel')}>Update Fuel Prices</button>
        <button className="action primary" onClick={() => setTab('specials')}>Update Specials</button>
        <button className="action primary" onClick={() => setTab('users')}>Manage Staff</button>
      </div>
    </div>
  );
}

function PasswordChangeRequired({ user, onChanged }: { user: SessionUser; onChanged: (user: SessionUser) => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const submit = async () => {
    try {
      await api.changePassword(currentPassword, newPassword);
      onChanged({ ...user, mustChangePassword: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password change failed');
    }
  };
  return (
    <main className="login">
      <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <span className="brand-mark">H</span>
        <h1>Set Your Password</h1>
        <p className="admin-help">This temporary password must be changed before management tools are available. Use at least 12 characters.</p>
        <input value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Temporary password" type="password" autoComplete="current-password" />
        <input value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="New password, 12+ characters" type="password" autoComplete="new-password" />
        {error && <p className="form-error">{error}</p>}
        <button className="action primary">Change Password</button>
      </form>
    </main>
  );
}

function Login({ onLogin, error, setError }: { onLogin: (user: SessionUser) => void; error: string; setError: (value: string) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  return (
    <main className="login">
      <form onSubmit={(event) => { event.preventDefault(); void api.login(username, password).then(onLogin).catch((err: Error) => setError(err.message)); }}>
        <span className="login-logo logo-heartland" role="img" aria-label="Heartland Express"><span aria-hidden="true">Heartland<br /><b>Express</b></span></span>
        <h1>Heartland Management</h1>
        <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Username" autoComplete="username" />
        <input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Password" type="password" autoComplete="current-password" />
        {error && <p className="form-error">{error}</p>}
        <button className="action primary">Log in</button>
      </form>
    </main>
  );
}

type DragState =
  | {
      type: 'item';
      id: number;
      categoryId: number;
      overId: number;
      name: string;
      price?: string;
      photoUrl?: string | null;
      x: number;
      y: number;
    }
  | {
      type: 'category';
      id: number;
      overId: number;
      name: string;
      x: number;
      y: number;
    };

type CategoryActionMenu = {
  id: number;
  top: number;
  left: number;
};

function categoryMenuPosition(button: HTMLElement | null, id: number): CategoryActionMenu | null {
  if (!button) return null;
  const rect = button.getBoundingClientRect();
  const menuWidth = 176;
  const menuHeight = 134;
  const gap = 6;
  const edge = 8;
  const left = Math.min(Math.max(edge, rect.right - menuWidth), Math.max(edge, window.innerWidth - menuWidth - edge));
  const below = rect.bottom + gap;
  const top = below + menuHeight > window.innerHeight - edge ? Math.max(edge, rect.top - menuHeight - gap) : below;
  return { id, top, left };
}

function moveById<T extends { id: number }>(rows: T[], id: number, targetId: number) {
  if (id === targetId) return rows;
  const from = rows.findIndex((row) => row.id === id);
  const to = rows.findIndex((row) => row.id === targetId);
  if (from < 0 || to < 0) return rows;
  const ordered = [...rows];
  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved);
  return ordered;
}

function MenuAdmin({ user }: { user: SessionUser }) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [editing, setEditing] = useState<Partial<MenuItem> | null>(null);
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Partial<MenuCategory> | null>(null);
  const [openCategoryActions, setOpenCategoryActions] = useState<CategoryActionMenu | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [quickPrice, setQuickPrice] = useState<{ id: number; value: string } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const categoryFormRef = useRef<HTMLFormElement | null>(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const canPrice = hasPermission(user, 'menu.price.edit');
  const canManage = hasPermission(user, 'menu.item.edit');
  const canCreate = hasPermission(user, 'menu.item.create');
  const canDelete = hasPermission(user, 'menu.item.delete');
  const canImages = hasPermission(user, 'menu.image.manage');
  const activeCategories = categories.filter((category) => category.active || canManage);

  const load = async () => {
    const [itemData, categoryData] = await Promise.all([api.items(), api.categories()]);
    setItems(itemData.items);
    setCategories(categoryData.categories);
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => { dragRef.current = drag; }, [drag]);

  useEffect(() => {
    if (!categoryEditorOpen || !editingCategory) return;
    window.requestAnimationFrame(() => {
      categoryFormRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      categoryFormRef.current?.querySelector<HTMLInputElement>('input[name="category-name"]')?.focus({ preventScroll: true });
    });
  }, [categoryEditorOpen, Boolean(editingCategory), editingCategory?.id]);

  useEffect(() => {
    if (!drag) return;
    const onMove = (event: PointerEvent) => {
      event.preventDefault();
      const current = dragRef.current;
      if (!current) return;
      const edge = 80;
      if (event.clientY < edge) window.scrollBy({ top: -18, behavior: 'auto' });
      if (window.innerHeight - event.clientY < edge) window.scrollBy({ top: 18, behavior: 'auto' });
      const element = document.elementFromPoint(event.clientX, event.clientY);
      if (current.type === 'item') {
        const target = element?.closest<HTMLElement>('[data-menu-item-id]');
        const categoryId = Number(target?.dataset.categoryId);
        const targetId = Number(target?.dataset.menuItemId);
        setDrag({ ...current, x: event.clientX, y: event.clientY, overId: targetId && categoryId === current.categoryId ? targetId : current.overId });
      } else {
        const target = element?.closest<HTMLElement>('[data-category-sort-id]');
        const targetId = Number(target?.dataset.categorySortId);
        setDrag({ ...current, x: event.clientX, y: event.clientY, overId: targetId || current.overId });
      }
    };
    const onUp = () => {
      const current = dragRef.current;
      setDrag(null);
      if (!current || current.id === current.overId) return;
      if (current.type === 'item') {
        const categoryItems = items.filter((item) => item.categoryId === current.categoryId);
        void reorderItems(current.categoryId, moveById(categoryItems, current.id, current.overId));
      } else {
        void reorderCategories(moveById(categories, current.id, current.overId));
      }
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp, { once: true });
    window.addEventListener('pointercancel', onUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, items, categories]);

  useEffect(() => {
    if (!openCategoryActions) return;
    const updatePosition = () => {
      setOpenCategoryActions((current) => {
        if (!current) return null;
        const trigger = document.querySelector<HTMLElement>(`[data-category-action-id="${current.id}"]`);
        if (!trigger) return null;
        const next = categoryMenuPosition(trigger, current.id);
        if (!next) return null;
        return next.top === current.top && next.left === current.left ? current : next;
      });
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('.category-action-popover') || target?.closest('[data-category-action-id]')) return;
      setOpenCategoryActions(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenCategoryActions(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openCategoryActions?.id]);

  const categoryName = (categoryId: number) => categories.find((category) => category.id === categoryId)?.name ?? '';
  const matches = (item: MenuItem) => {
    const text = `${item.name} ${item.description ?? ''} ${item.categoryName ?? categoryName(item.categoryId)}`.toLowerCase();
    return (!categoryFilter || String(item.categoryId) === categoryFilter) && (!query.trim() || text.includes(query.trim().toLowerCase()));
  };
  const grouped = activeCategories
    .map((category) => ({ category, items: items.filter((item) => item.categoryId === category.id && matches(item)) }))
    .filter(({ items: categoryItems }) => categoryItems.length || !query.trim() && (!categoryFilter || String(categoryFilter) === ''));

  const startAdd = () => {
    const categoryId = Number(categoryFilter || activeCategories[0]?.id);
    setEditing({ categoryId, name: '', description: '', price: '0.00', photoUrl: null, availability: 'available', featured: false, active: true, displayOrder: ((items.filter((item) => item.categoryId === categoryId).length + 1) * 10) });
  };
  const startEdit = (item: MenuItem) => setEditing({ ...item });
  const saveItem = async () => {
    if (!editing) return;
    setError('');
    await api.saveItem({
      ...editing,
      categoryId: Number(editing.categoryId),
      name: String(editing.name ?? ''),
      description: editing.description ?? '',
      price: String(editing.price ?? '0'),
      photoUrl: editing.photoUrl ?? null,
      availability: String(editing.availability ?? 'available'),
      featured: Boolean(editing.featured),
      active: Boolean(editing.active),
      displayOrder: Number(editing.displayOrder ?? 0)
    }).then(async () => { setEditing(null); setSaved('Menu item saved.'); await load(); }).catch((err: Error) => setError(err.message));
  };
  const savePrice = async (item: MenuItem, price: string) => {
    setError('');
    await api.updatePrice(item.id, price).then(async () => {
      setQuickPrice(null);
      setSaved('Price saved.');
      await load();
    }).catch((err: Error) => setError(err.message));
  };
  const uploadItemPhoto = async (item: Partial<MenuItem>, file?: File) => {
    if (!item.id || !file) return;
    await api.uploadItemPhoto(item.id, file);
    setSaved('Photo processed and saved.');
    await load();
    const fresh = (await api.items()).items.find((row) => row.id === item.id);
    if (fresh) setEditing({ ...fresh });
  };
  const removeItemPhoto = async (item: Partial<MenuItem>) => {
    if (!item.id || !confirm('Remove this menu photo?')) return;
    await api.removeItemPhoto(item.id);
    setSaved('Photo removed.');
    await load();
    setEditing({ ...item, photoUrl: null });
  };
  const archiveItem = async (item: MenuItem) => {
    if (!confirm(`Archive ${item.name}? It will stop showing publicly but audit history remains.`)) return;
    await api.deleteItem(item.id);
    setSaved('Menu item archived.');
    await load();
    setEditing(null);
  };
  const reorderItems = async (categoryId: number, ordered: MenuItem[]) => {
    setItems((current) => current.map((item) => item.categoryId === categoryId ? ordered.find((row) => row.id === item.id) ?? item : item));
    await api.reorderItems(categoryId, ordered.map((item) => item.id));
    setSaved('Menu order saved.');
    await load();
  };
  const moveItem = async (categoryId: number, itemId: number, delta: number) => {
    const categoryItems = items.filter((item) => item.categoryId === categoryId);
    const index = categoryItems.findIndex((item) => item.id === itemId);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= categoryItems.length) return;
    const ordered = [...categoryItems];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(nextIndex, 0, moved);
    await reorderItems(categoryId, ordered);
  };
  const reorderCategories = async (ordered: MenuCategory[]) => {
    setCategories(ordered);
    await api.reorderCategories(ordered.map((category) => category.id));
    setSaved('Category order saved.');
    await load();
  };
  const moveCategory = async (categoryId: number, delta: number) => {
    const index = categories.findIndex((category) => category.id === categoryId);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= categories.length) return;
    const ordered = [...categories];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(nextIndex, 0, moved);
    await reorderCategories(ordered);
  };
  const saveCategory = async () => {
    if (!editingCategory) return;
    const displayOrder = Number(editingCategory.displayOrder ?? ((categories.length + 1) * 10));
    await api.saveCategory({ ...editingCategory, name: String(editingCategory.name ?? ''), description: editingCategory.description ?? '', displayOrder, active: Boolean(editingCategory.active ?? true) });
    setEditingCategory(null);
    setOpenCategoryActions(null);
    setSaved('Category saved.');
    await load();
  };
  const toggleCategoryActive = async (category: MenuCategory) => {
    await api.saveCategory({ ...category, active: !category.active });
    setOpenCategoryActions(null);
    setSaved(category.active ? 'Category disabled.' : 'Category enabled.');
    await load();
  };
  const archiveCategory = async (category: MenuCategory) => {
    const count = items.filter((item) => item.categoryId === category.id && item.active).length;
    const message = count ? `${category.name} contains ${count} active item${count === 1 ? '' : 's'}. Archive the category anyway? Items remain stored and auditable.` : `Archive ${category.name}?`;
    if (!confirm(message)) return;
    await api.saveCategory({ ...category, active: false });
    setOpenCategoryActions(null);
    setSaved('Category archived.');
    await load();
  };
  const startItemDrag = (event: React.PointerEvent, item: MenuItem) => {
    if (!canManage || event.button !== 0) return;
    event.preventDefault();
    setDrag({ type: 'item', id: item.id, categoryId: item.categoryId, overId: item.id, name: item.name, price: item.price, photoUrl: item.photoUrl, x: event.clientX, y: event.clientY });
  };
  const startCategoryDrag = (event: React.PointerEvent, category: MenuCategory) => {
    if (!canManage || event.button !== 0) return;
    event.preventDefault();
    setDrag({ type: 'category', id: category.id, overId: category.id, name: category.name, x: event.clientX, y: event.clientY });
  };
  const toggleCollapsed = (categoryId: number) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(categoryId)) next.delete(categoryId);
    else next.add(categoryId);
    return next;
  });
  const openCategoryMenu = (event: React.MouseEvent<HTMLButtonElement>, categoryId: number) => {
    event.preventDefault();
    event.stopPropagation();
    const nextPosition = categoryMenuPosition(event.currentTarget, categoryId);
    setOpenCategoryActions((current) => current?.id === categoryId ? null : nextPosition);
  };
  const activeCategoryAction = openCategoryActions ? categories.find((category) => category.id === openCategoryActions.id) : null;

  return (
    <div className="manager-stack">
      <div className="menu-command-bar">
        <label className="search-box"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search menu items..." /></label>
        <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} aria-label="Filter category">
          <option value="">All Categories</option>
          {activeCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
        </select>
        {canManage && <button className="action ghost" onClick={() => setCategoryEditorOpen(true)} type="button">Manage Categories</button>}
        {canCreate && <button className="action primary" onClick={startAdd} type="button"><Plus size={16} /> Add Menu Item</button>}
      </div>
      <p className="admin-help">Staff can update existing prices only. Managers can edit content, images, availability, featured state, and ordering.</p>
      {saved && <p className="save-state">{saved}</p>}
      {error && <p className="form-error">{error}</p>}
      {grouped.map(({ category, items: categoryItems }) => {
        const isCollapsed = collapsed.has(category.id);
        return (
          <section className={`menu-manage-section ${isCollapsed ? 'is-collapsed' : ''}`} key={category.id}>
            <button className="category-section-head" onClick={() => toggleCollapsed(category.id)} type="button">
              <span>
                <strong>{category.name}</strong>
                <small>{categoryItems.length} item{categoryItems.length === 1 ? '' : 's'}{category.active ? '' : ' Â· archived'}</small>
              </span>
              {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
            {!isCollapsed && <div className="menu-card-list">
              {categoryItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {drag?.type === 'item' && drag.overId === item.id && drag.id !== item.id && <div className="drop-placeholder">Drop here</div>}
                  <article className={`menu-manage-card ${drag?.type === 'item' && drag.id === item.id ? 'is-origin' : ''}`} data-menu-item-id={item.id} data-category-id={category.id}>
                    {canManage ? <button className="drag-handle" onPointerDown={(event) => startItemDrag(event, item)} aria-label={`Drag ${item.name}`}><GripVertical size={20} /></button> : <span />}
                    <button className="thumb-button" disabled={!canManage} onClick={() => startEdit(item)}>{item.photoUrl ? <img src={item.photoUrl} alt="" /> : <span className="thumb-placeholder"><Camera size={20} /></span>}</button>
                    <div className="menu-card-main">
                      <strong>{item.name}</strong>
                      <div className="menu-price-line">
                        {quickPrice?.id === item.id ? (
                          <span className="inline-price-edit">
                            <input aria-label={`${item.name} price`} inputMode="decimal" value={quickPrice.value} onChange={(event) => setQuickPrice({ id: item.id, value: event.target.value })} />
                            <button className="action primary" onClick={() => void savePrice(item, quickPrice.value)} type="button">Save</button>
                            <button className="action ghost" onClick={() => setQuickPrice(null)} type="button">Cancel</button>
                          </span>
                        ) : (
                          <button className="price-button" disabled={!canPrice} onClick={() => setQuickPrice({ id: item.id, value: item.price })} type="button">Current price: ${item.price}</button>
                        )}
                      </div>
                      <div className="status-line"><span>{item.active ? 'Available' : 'Disabled'}</span>{item.featured && <span>Featured</span>}</div>
                    </div>
                    <div className="card-actions">
                      {canManage && <button className="action ghost" onClick={() => startEdit(item)} type="button">Edit</button>}
                      {canManage && <button className="icon-button" disabled={index === 0} onClick={() => void moveItem(category.id, item.id, -1)} aria-label={`Move ${item.name} up`}><ChevronUp size={16} /></button>}
                      {canManage && <button className="icon-button" disabled={index === categoryItems.length - 1} onClick={() => void moveItem(category.id, item.id, 1)} aria-label={`Move ${item.name} down`}><ChevronDown size={16} /></button>}
                    </div>
                  </article>
                </React.Fragment>
              ))}
            </div>}
          </section>
        );
      })}
      {!grouped.some((group) => group.items.length) && <p className="empty-state">No menu items match that search.</p>}
      {categoryEditorOpen && <div className="drawer-backdrop" role="presentation"><div className="editor-drawer category-drawer">
        <div className="drawer-head"><h2>Manage Categories</h2><button className="action ghost" type="button" onClick={() => { setCategoryEditorOpen(false); setEditingCategory(null); }}>Close</button></div>
        <button className="action primary category-add-button" type="button" onClick={() => setEditingCategory({ name: '', description: '', displayOrder: (categories.length + 1) * 10, active: true })}><Plus size={16} /> Add Category</button>
        {editingCategory && <form ref={categoryFormRef} className="editor-card category-editor-card" onSubmit={(event) => { event.preventDefault(); void saveCategory(); }}>
          <div className="category-editor-heading">
            <span>{editingCategory.id ? 'Editing category' : 'New category'}</span>
            <strong>{editingCategory.id ? String(editingCategory.name ?? '') : 'Add Category'}</strong>
          </div>
          <label>Category name<input name="category-name" value={String(editingCategory.name ?? '')} onChange={(event) => setEditingCategory({ ...editingCategory, name: event.target.value })} required /></label>
          <label>Display order<input inputMode="numeric" type="number" value={Number(editingCategory.displayOrder ?? 0)} onChange={(event) => setEditingCategory({ ...editingCategory, displayOrder: Number(event.target.value) })} /></label>
          <label className="check"><input type="checkbox" checked={Boolean(editingCategory.active ?? true)} onChange={(event) => setEditingCategory({ ...editingCategory, active: event.target.checked })} /> Enabled</label>
          {editingCategory.id && <div className="move-controls"><button className="action ghost" type="button" onClick={() => void moveCategory(Number(editingCategory.id), -1)}><ChevronUp size={16} /> Move Up</button><button className="action ghost" type="button" onClick={() => void moveCategory(Number(editingCategory.id), 1)}><ChevronDown size={16} /> Move Down</button></div>}
          <div className="drawer-actions"><button className="action primary"><Save size={16} /> Save Category</button><button className="action ghost" type="button" onClick={() => setEditingCategory(null)}>Cancel</button></div>
        </form>}
        <div className="category-sort-list">
          {categories.map((category) => (
            <article className={`category-sort-card ${drag?.type === 'category' && drag.id === category.id ? 'is-origin' : ''}`} data-category-sort-id={category.id} key={category.id}>
              {drag?.type === 'category' && drag.overId === category.id && drag.id !== category.id && <div className="drop-placeholder">Drop here</div>}
              <button className="drag-handle" onPointerDown={(event) => startCategoryDrag(event, category)} aria-label={`Drag ${category.name}`}><GripVertical size={20} /></button>
              <div className="category-sort-main"><strong>{category.name}</strong><small>{items.filter((item) => item.categoryId === category.id).length} items · {category.active ? 'Enabled' : 'Disabled'}</small></div>
              <div className="overflow-wrap">
                <button className="icon-button overflow-button" data-category-action-id={category.id} type="button" aria-label={`Actions for ${category.name}`} aria-expanded={openCategoryActions?.id === category.id} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => openCategoryMenu(event, category.id)}><MoreVertical size={18} /></button>
              </div>
            </article>
          ))}
        </div>
      </div></div>}
      {openCategoryActions && activeCategoryAction && createPortal(
        <div className="category-action-popover" style={{ top: openCategoryActions.top, left: openCategoryActions.left }}>
          <button type="button" onClick={() => { setEditingCategory({ ...activeCategoryAction }); setOpenCategoryActions(null); }}>Edit</button>
          <button type="button" onClick={() => void toggleCategoryActive(activeCategoryAction)}>{activeCategoryAction.active ? 'Disable' : 'Enable'}</button>
          <button className="danger-text" type="button" onClick={() => void archiveCategory(activeCategoryAction)}>Archive</button>
        </div>,
        document.body
      )}
      {editing && <div className="drawer-backdrop" role="presentation"><form className="editor-drawer" onSubmit={(event) => { event.preventDefault(); void saveItem(); }}>
        <div className="drawer-head"><h2>{editing.id ? 'Edit Menu Item' : 'Add Menu Item'}</h2><button className="action ghost" type="button" onClick={() => setEditing(null)}>Cancel</button></div>
        <div className="image-editor">
          {editing.photoUrl ? <img src={editing.photoUrl} alt="" /> : <span className="thumb-placeholder"><Camera size={22} /></span>}
          {editing.id && canImages && <label className="action ghost file-action">{editing.photoUrl ? 'Replace Photo' : 'Upload Photo'}<input type="file" accept="image/*" capture="environment" onChange={(event) => void uploadItemPhoto(editing, event.target.files?.[0])} /></label>}
          {editing.id && canImages && <button className="action ghost" type="button" disabled={!editing.photoUrl} onClick={() => void removeItemPhoto(editing)}>Remove Photo</button>}
          {!editing.id && <p className="admin-help">Save the item once before uploading a photo.</p>}
        </div>
        <label>Item name<input value={String(editing.name ?? '')} onChange={(event) => setEditing({ ...editing, name: event.target.value })} disabled={!canManage} required /></label>
        <label>Category<select value={editing.categoryId ?? ''} onChange={(event) => setEditing({ ...editing, categoryId: Number(event.target.value) })} disabled={!canManage}>{activeCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
        <label>Description<textarea value={editing.description ?? ''} onChange={(event) => setEditing({ ...editing, description: event.target.value })} disabled={!canManage} /></label>
        <label>Price<input inputMode="decimal" value={String(editing.price ?? '')} onChange={(event) => setEditing({ ...editing, price: event.target.value })} disabled={!canPrice} required /></label>
        <label>Display order<input inputMode="numeric" type="number" value={Number(editing.displayOrder ?? 0)} onChange={(event) => setEditing({ ...editing, displayOrder: Number(event.target.value) })} disabled={!canManage} /></label>
        {editing.id && canManage && <div className="move-controls"><button className="action ghost" type="button" onClick={() => void moveItem(Number(editing.categoryId), Number(editing.id), -1)}><ChevronUp size={16} /> Move Up</button><button className="action ghost" type="button" onClick={() => void moveItem(Number(editing.categoryId), Number(editing.id), 1)}><ChevronDown size={16} /> Move Down</button></div>}
        <div className="form-grid"><label className="check"><input type="checkbox" checked={Boolean(editing.active)} onChange={(event) => setEditing({ ...editing, active: event.target.checked })} disabled={!canManage} /> Available</label><label className="check"><input type="checkbox" checked={Boolean(editing.featured)} onChange={(event) => setEditing({ ...editing, featured: event.target.checked })} disabled={!canManage} /> Featured</label></div>
        <div className="drawer-actions"><button className="action primary"><Save size={16} /> Save</button>{editing.id && canDelete && <button className="action danger" type="button" onClick={() => void archiveItem(editing as MenuItem)}><Trash2 size={16} /> Archive</button>}</div>
      </form></div>}
      {drag && <div className="drag-overlay" style={{ transform: `translate3d(${drag.x + 14}px, ${drag.y + 14}px, 0)` }}>
        {drag.type === 'item' ? <><span className="overlay-thumb">{drag.photoUrl ? <img src={drag.photoUrl} alt="" /> : <Camera size={18} />}</span><strong>{drag.name}</strong><small>${drag.price}</small></> : <><GripVertical size={18} /><strong>{drag.name}</strong></>}
      </div>}
    </div>
  );
}

function FuelAdmin({ user }: { user: SessionUser }) {
  const [prices, setPrices] = useState<FuelPrice[]>([]);
  const [priceInputs, setPriceInputs] = useState<Partial<Record<FuelGrade, string>>>({});
  const [history, setHistory] = useState<FuelHistoryRecord[]>([]);
  const [grade, setGrade] = useState('');
  const [saved, setSaved] = useState('');
  const [sync, setSync] = useState<FuelSyncStatus | null>(null);
  const [syncError, setSyncError] = useState('');
  const [priceError, setPriceError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [pricesReady, setPricesReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const refreshSync = () => api.fuelSync().then(data => { setSync(data); setSyncError(''); }).catch(() => setSyncError('Update status unavailable. Manual price editing is still available.'));
  const [error, setError] = useState('');
  const canEdit = hasPermission(user, 'fuel.manage');
  const refreshPrices = async () => {
    setPriceError('');
    try { const data = await api.fuelPrices(); setPrices(data.prices); setPriceInputs({}); setPricesReady(true); }
    catch { setPricesReady(false); setPriceError('Fuel prices could not be loaded. Please retry.'); }
  };
  const refreshHistory = async () => {
    setHistoryError('');
    try { const data = await api.fuelHistory(grade || undefined); setHistory(data.history); }
    catch { setHistoryError('Price history is unavailable. You can still edit prices.'); }
  };
  useEffect(() => { void refreshPrices(); void refreshSync(); }, []);
  useEffect(() => { void refreshHistory(); }, [grade]);
  const update = (grade: FuelGrade, patch: Partial<FuelPrice>) => {
    if (patch.price !== undefined) setPriceInputs(current => ({ ...current, [grade]: patch.price ?? '' }));
    setSaved('');
    setError('');
    setPrices((current) => current.map((item) => item.grade === grade ? { ...item, ...patch } : item));
  };
  const save = async () => {
    if (busy || !pricesReady) return;
    setBusy(true);
    setSaved('');
    setError('');
    try {
      const data = await api.saveFuelPrices(prices.map((price) => ({ grade: price.grade, price: price.price || null, enabled: Boolean(price.enabled) })));
      setPrices(data.prices);
      setPriceInputs({});
      setSaved('Credit prices saved. Automatic updates are paused until you resume them.');
      await refreshSync();
      await refreshHistory();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Fuel prices could not be saved.');
    } finally { setBusy(false); }
  };
  const changeMode = async (mode: 'disabled' | 'automatic') => {
    if (busy) return;
    setBusy(true); setError(''); setSaved('');
    try { setSync(await api.setFuelSyncMode(mode)); }
    catch { setError('Automatic update mode could not be changed.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="manager-stack">
      <section className="fuel-sync-card" aria-label="Automatic fuel update status">
        <h2>Credit fuel prices</h2>
        <p>Manual entries take priority and pause automatic updates.</p>
        {sync ? <>
          <strong>{sync.state.mode === 'manual' ? 'Manual override active' : sync.config.providerStatus === 'blocked' ? 'Automatic source paused: access blocked' : sync.config.providerStatus === 'not_configured' ? 'Automatic source not configured' : `Automatic updates: ${sync.health.status}`}</strong>
          <small>Last successful price check: {sync.state.lastSuccessAt ? formatDate(sync.state.lastSuccessAt) : 'No automatic check yet'}</small>
          <small>Last scheduler check: {sync.state.lastInvocationAt ? formatDate(sync.state.lastInvocationAt) : 'Awaiting secure setup'}{sync.state.lastInvocationOutcome ? ` (${sync.state.lastInvocationOutcome.replaceAll('_', ' ')})` : ''}</small>
          {Object.entries(sync.state.sourceUpdatedAt).map(([grade, at]) => <small key={grade}>{grade} source report: {formatDate(at)}</small>)}
          <small>Email and SMS alerts are disabled pending recipient setup.</small>
          {sync.state.failureCode && <p role="status">Latest issue: {sync.state.failureCode.replaceAll('_', ' ')}</p>}
          {canEdit && <div className="toolbar"><button className="action" disabled={busy || sync.state.mode === 'disabled'} onClick={() => void changeMode('disabled')}>Pause automatic updates</button><button className="action" disabled={busy || sync.config.providerStatus !== 'ready' || sync.state.mode === 'automatic'} onClick={() => void changeMode('automatic')}>Resume automatic updates</button></div>}
        </> : <p role="status">{syncError || 'Loading update status…'}</p>}
      </section>
      {!pricesReady && !priceError && <p role="status">Loading fuel prices.</p>}
      {priceError && <div role="alert"><p className="form-error">{priceError}</p><button className="action" onClick={() => void refreshPrices()}>Retry fuel prices</button></div>}
      {syncError && <button className="action" onClick={() => void refreshSync()}>Retry update status</button>}
      <div className="fuel-card-grid">{prices.filter((price) => price.enabled || canEdit).map((price) => (
        <article className="fuel-admin-card" key={price.grade}>
          <span>{price.label}</span>
          <strong>{formatFuelPrice(price.price)}</strong>
          <small>Updated {formatDate(price.lastUpdatedAt)}</small>
          {canEdit && <><input aria-label={`${price.label} credit price`} inputMode="decimal" disabled={busy || !pricesReady} value={priceInputs[price.grade] ?? formatFuelAmount(price.price) ?? ''} onChange={(event) => update(price.grade, { price: event.target.value })} placeholder="0.00" /><label className="check"><input disabled={busy || !pricesReady} type="checkbox" checked={price.enabled} onChange={(event) => update(price.grade, { enabled: event.target.checked })} /> Display</label></>}
        </article>
      ))}</div>
      {canEdit ? <button className="action primary" disabled={busy || !pricesReady} onClick={() => void save()}><Save size={16} /> Update Prices</button> : <p className="admin-help">Fuel prices are read-only for your account.</p>}
      {saved && <p className="save-state">{saved}</p>}
      {error && <p className="form-error">{error}</p>}
      <section className="history-section">
        <div className="toolbar"><h2>Fuel Price History - Last 30 Days</h2><select value={grade} onChange={(event) => setGrade(event.target.value)}><option value="">All grades</option>{prices.map((price) => <option key={price.grade} value={price.grade}>{price.label}</option>)}</select></div>
        {historyError && <div role="status"><p>{historyError}</p><button className="action" onClick={() => void refreshHistory()}>Retry price history</button></div>}
        {!historyError && <p className="admin-help">{history.length} price changes</p>}
        <div className="history-list">{history.map((row) => <article className="history-card" key={row.id}><time>{formatDate(row.createdAt)}</time><strong>{String(row.newValue?.grade ?? row.entityId)}</strong><span>{formatFuelPrice(row.previousValue?.price)} -&gt; {formatFuelPrice(row.newValue?.price)}</span><small>{actorLabel(row.username)}</small></article>)}</div>
      </section>
    </div>
  );
}

function SpecialsAdmin({ user }: { user: SessionUser }) {
  const blank = { name: '', description: '', price: '', photoUrl: '', startDate: '', startTime: '', endDate: '', endTime: '', originalStart: '', originalEnd: '', active: true, featured: false };
  const [specials, setSpecials] = useState<Special[]>([]);
  const [draft, setDraft] = useState<Record<string, string | boolean>>(blank);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const canManage = hasPermission(user, 'specials.manage');
  const load = () => api.specials().then((data) => setSpecials(data.specials));
  useEffect(() => { void load(); }, []);
  const edit = (special: Special) => {
    setError(''); setSaved('');
    const start = splitSpecialDateTime(special.startsAt);
    const end = splitSpecialDateTime(special.endsAt);
    setDraft({ id: String(special.id), name: special.name, description: special.description ?? '', price: special.price ?? '', photoUrl: special.photoUrl ?? '', startDate: start.date, startTime: start.time, endDate: end.date, endTime: end.time, originalStart: special.startsAt ?? '', originalEnd: special.endsAt ?? '', active: Boolean(special.active), featured: Boolean(special.featured) });
  };
  const save = async () => {
    if (saving) return;
    setError(''); setSaved(''); setSaving(true);
    try {
      const startsAt = combineSpecialDateTime(String(draft.startDate || ''), String(draft.startTime || ''), String(draft.originalStart || ''));
      const endsAt = combineSpecialDateTime(String(draft.endDate || ''), String(draft.endTime || ''), String(draft.originalEnd || ''));
      validateSpecialDateRange(startsAt, endsAt);
      await api.saveSpecial({ id: draft.id ? Number(draft.id) : undefined, name: String(draft.name), description: String(draft.description || ''), price: draft.price ? String(draft.price) : null, photoUrl: draft.photoUrl ? String(draft.photoUrl) : null, startsAt, endsAt, active: Boolean(draft.active), featured: Boolean(draft.featured) } as Partial<Special>);
      setDraft(blank); setSaved('Special saved.'); await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The special could not be saved. Please try again.');
    } finally { setSaving(false); }
  };
  const upload = async (id: number, file?: File) => { if (!file) return; await api.uploadSpecialPhoto(id, file); setSaved('Photo processed and saved.'); await load(); };
  const removePhoto = async (id: number) => { if (!confirm('Remove this special photo?')) return; await api.removeSpecialPhoto(id); setSaved('Photo removed.'); await load(); };
  return (
    <div className="admin-two-col">
      {canManage && <form className="editor-card" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <h2>{draft.id ? 'Edit Special' : 'Create Special'}</h2>
        <label>Name<input value={String(draft.name)} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required /></label>
        <label>Description<textarea value={String(draft.description)} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
        <label>Price<input value={String(draft.price)} onChange={(event) => setDraft({ ...draft, price: event.target.value })} /></label>
        <p className="schedule-zone">All special times are Central Time (America/Chicago), including daylight saving time. A blank time means midnight at the start of the selected date.</p>
        <div className="form-grid"><label>Start date<input type="date" value={String(draft.startDate)} onChange={(event) => setDraft({ ...draft, startDate: event.target.value })} /></label><label>Start time (Central)<input type="time" value={String(draft.startTime)} onChange={(event) => setDraft({ ...draft, startTime: event.target.value })} /></label></div>
        <div className="form-grid"><label>End date<input type="date" value={String(draft.endDate)} onChange={(event) => setDraft({ ...draft, endDate: event.target.value })} /></label><label>End time (Central)<input type="time" value={String(draft.endTime)} onChange={(event) => setDraft({ ...draft, endTime: event.target.value })} /></label></div>
        <label className="check"><input type="checkbox" checked={Boolean(draft.active)} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Active</label>
        <label className="check"><input type="checkbox" checked={Boolean(draft.featured)} onChange={(event) => setDraft({ ...draft, featured: event.target.checked })} /> Featured</label>
        <button className="action primary" disabled={saving}><Save size={16} /> {saving ? 'Saving…' : 'Save Special'}</button>
        {error && <p role="alert" className="status-bad">{error}</p>}
        {saved && <p className="save-state">{saved}</p>}
      </form>}
      <div className="table">
        {specials.map((item) => <div className="row special-manager-row" key={item.id}><strong>{item.name}</strong><span>{item.price ? `$${item.price}` : 'No price'}</span><span>{item.active ? 'Active' : 'Inactive'} / {item.featured ? 'Featured' : 'Standard'}</span>{item.photoUrl ? <img className="admin-thumb" src={item.photoUrl} alt="" /> : <span>No photo</span>}<button className="action ghost" onClick={() => edit(item)} type="button" disabled={!canManage}>Edit</button><label className="action ghost file-action">Upload Photo<input type="file" accept="image/*" onChange={(event) => void upload(item.id, event.target.files?.[0])} disabled={!canManage} /></label><button className="action ghost" onClick={() => void removePhoto(item.id)} type="button" disabled={!canManage || !item.photoUrl}>Remove Photo</button></div>)}
      </div>
    </div>
  );
}

function UsersAdmin({ user }: { user: SessionUser }) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [mode, setMode] = useState<'list' | 'create' | 'edit' | 'permissions' | 'password'>('list');
  const [selected, setSelected] = useState<ManagedUser | null>(null);
  const [draft, setDraft] = useState({ username: '', displayName: '', password: '', role: 'user' as SessionUser['role'], disabled: false, forcePasswordChange: true });
  const [password, setPassword] = useState('');
  const [permissionDraft, setPermissionDraft] = useState<Permission[]>([]);
  const [saved, setSaved] = useState('');
  const load = () => api.managedUsers().then((data) => setUsers(data.users));
  useEffect(() => { void load(); }, []);
  const startEdit = (item: ManagedUser) => { setSelected(item); setDraft({ username: item.username, displayName: item.displayName, password: '', role: item.role, disabled: Boolean(item.disabledAt), forcePasswordChange: false }); setMode('edit'); };
  const startPermissions = (item: ManagedUser) => { setSelected(item); setPermissionDraft(item.permissions); setMode('permissions'); };
  const startPassword = (item: ManagedUser) => { setSelected(item); setPassword(''); setMode('password'); };
  const create = async () => { await api.createUser(draft); setMode('list'); setSaved('Account created.'); await load(); };
  const saveEdit = async (archived = Boolean(selected?.archivedAt)) => { if (!selected) return; await api.updateUser(selected.id, { displayName: draft.displayName, role: draft.role, disabled: draft.disabled, archived, password: '', forcePasswordChange: false }); setMode('list'); setSaved('Account updated.'); await load(); };
  const savePermissions = async () => { if (!selected) return; await api.updateUserPermissions(selected.id, permissionDraft); setMode('list'); setSaved('Permissions saved.'); await load(); };
  const resetPassword = async () => { if (!selected) return; await api.resetUserPassword(selected.id, password, true); setMode('list'); setSaved('Temporary password saved.'); await load(); };
  const togglePermission = (permission: Permission) => setPermissionDraft(permissionDraft.includes(permission) ? permissionDraft.filter((item) => item !== permission) : [...permissionDraft, permission]);
  const permissionLabel = (permission: Permission) => ({
    'menu.view': 'Menu viewing',
    'menu.price.edit': 'Menu price editing',
    'menu.item.create': 'Menu item creation',
    'menu.item.edit': 'Menu content editing',
    'menu.item.delete': 'Menu item archive',
    'menu.image.manage': 'Menu image management',
    'specials.view': 'View specials',
    'specials.manage': 'Manage specials',
    'fuel.view': 'View fuel',
    'fuel.manage': 'Manage fuel',
    'users.view': 'View staff',
    'users.manage': 'Manage staff',
    'audit.view': 'View audit',
    'releases.view': 'View releases',
    'support.view': 'View support',
    'config.manage': 'Internal configuration'
  }[permission]);
  return (
    <div className="manager-stack">
      <div className="toolbar sticky-toolbar"><button className="action primary" onClick={() => { setDraft({ username: '', displayName: '', password: '', role: 'user', disabled: false, forcePasswordChange: true }); setMode('create'); }}><UserPlus size={16} /> Add Staff Member</button>{saved && <span className="save-state">{saved}</span>}</div>
      {mode === 'list' && <div className="staff-card-list">{users.map((item) => <article className="staff-card" key={item.id}>
        <div><strong>{customerText(item.displayName)}</strong><span>{actorLabel(item.username)}</span></div>
        <span className="pill">{displayRole(item.role)}</span>
        <span className={item.archivedAt || item.disabledAt ? 'status-bad' : 'status-good'}>{item.archivedAt ? 'Archived' : item.disabledAt ? 'Disabled' : 'Active'}</span>
        <small>Last login: {formatDate(item.lastLoginAt)}</small>
        <div className="staff-actions">
          <button className="action ghost" onClick={() => startEdit(item)}>Edit</button>
          <button className="action ghost" onClick={() => startPermissions(item)} disabled={item.role === 'manager'}>Permissions</button>
          <button className="action ghost" onClick={() => startPassword(item)}>Reset Password</button>
          <button className="action ghost" disabled={item.id === user.id} onClick={() => { if (confirm(`${item.disabledAt ? 'Enable' : 'Disable'} ${customerText(item.displayName)}?`)) void api.updateUser(item.id, { displayName: item.displayName, role: item.role, disabled: !item.disabledAt, archived: Boolean(item.archivedAt), password: '', forcePasswordChange: false }).then(async () => { setSaved(item.disabledAt ? 'Account enabled.' : 'Account disabled.'); await load(); }); }}>{item.disabledAt ? 'Enable' : 'Disable'}</button>
          <button className="action danger" disabled={item.id === user.id || Boolean(item.archivedAt)} onClick={() => { if (confirm(`Archive ${customerText(item.displayName)}? Archived accounts cannot log in, but audit history remains.`)) void api.updateUser(item.id, { displayName: item.displayName, role: item.role, disabled: Boolean(item.disabledAt), archived: true, password: '', forcePasswordChange: false }).then(async () => { setSaved('Account archived.'); await load(); }); }}>Archive</button>
        </div>
      </article>)}</div>}
      {(mode === 'create' || mode === 'edit') && <div className="drawer-backdrop" role="presentation"><form className="editor-drawer" onSubmit={(event) => { event.preventDefault(); void (mode === 'create' ? create() : saveEdit()); }}><div className="drawer-head"><h2>{mode === 'create' ? 'Add Staff Member' : 'Edit Person'}</h2><button className="action ghost" type="button" onClick={() => setMode('list')}>Cancel</button></div><section><h3>Profile</h3>{mode === 'create' && <label>Username<input value={draft.username} onChange={(event) => setDraft({ ...draft, username: event.target.value })} required /></label>}<label>Display name<input value={customerText(draft.displayName)} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} required /></label><label>Role<select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as SessionUser['role'] })}><option value="manager">Manager</option><option value="user">Staff</option></select></label></section><section><h3>Account</h3>{mode === 'create' && <label>Temporary password<input type="password" value={draft.password} onChange={(event) => setDraft({ ...draft, password: event.target.value })} required minLength={12} /></label>}<label className="check"><input type="checkbox" checked={draft.forcePasswordChange} onChange={(event) => setDraft({ ...draft, forcePasswordChange: event.target.checked })} /> Force password change</label><label className="check"><input type="checkbox" checked={draft.disabled} onChange={(event) => setDraft({ ...draft, disabled: event.target.checked })} /> Disabled</label></section><div className="drawer-actions"><button className="action primary">Save</button></div></form></div>}
      {mode === 'permissions' && selected && <div className="drawer-backdrop" role="presentation"><div className="editor-drawer"><div className="drawer-head"><h2>Permissions for {customerText(selected.displayName)}</h2><button className="action ghost" onClick={() => setMode('list')}>Cancel</button></div><p className="admin-help">Manager accounts have full access. Staff can receive specific capabilities as needed.</p><div className="permission-grid">{allPermissions.filter((permission) => permission !== 'config.manage').map((permission) => <label className="check toggle-card" key={permission}><input type="checkbox" checked={permissionDraft.includes(permission)} onChange={() => togglePermission(permission)} /> {permissionLabel(permission)}</label>)}</div><div className="drawer-actions"><button className="action primary" onClick={() => void savePermissions()}>Save Permissions</button></div></div></div>}
      {mode === 'password' && selected && <div className="drawer-backdrop" role="presentation"><form className="editor-drawer" onSubmit={(event) => { event.preventDefault(); void resetPassword(); }}><div className="drawer-head"><h2>Reset Password</h2><button className="action ghost" type="button" onClick={() => setMode('list')}>Cancel</button></div><p>For {customerText(selected.displayName)}</p><label>Temporary password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="12+ characters" required minLength={12} /></label><p className="admin-help">The person must change this temporary password at next login.</p><div className="drawer-actions"><button className="action primary">Reset Password</button></div></form></div>}
    </div>
  );
}

function AuditAdmin() {
  const [logs, setLogs] = useState<Array<Record<string, unknown>>>([]);
  const [filters, setFilters] = useState({ username: '', action: '', entityType: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [filters]);
  useEffect(() => { void api.auditLogs().then((data) => setLogs(data.logs as Array<Record<string, unknown>>)); }, []);
  const filtered = logs.filter((row) => (!filters.username || actorLabel(row.username).toLowerCase().includes(filters.username.toLowerCase())) && (!filters.action || String(row.action ?? '').includes(filters.action)) && (!filters.entityType || String(row.entityType ?? '').includes(filters.entityType)) && (!filters.dateFrom || new Date(String(row.createdAt)) >= new Date(filters.dateFrom)) && (!filters.dateTo || new Date(String(row.createdAt)) <= new Date(filters.dateTo)));
  const rows = filtered.slice(page * 20, page * 20 + 20);
  return <div className="manager-stack"><div className="filter-grid"><input placeholder="User" value={filters.username} onChange={(e) => setFilters({ ...filters, username: e.target.value })} /><input placeholder="Action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} /><input placeholder="Entity" value={filters.entityType} onChange={(e) => setFilters({ ...filters, entityType: e.target.value })} /><input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} /><input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} /></div><div className="table audit-table">{rows.map((row) => <details className="audit-row" key={String(row.id)}><summary><span>{formatDate(String(row.createdAt))}</span><span>{actorLabel(row.username)}</span><span>{row.role === 'system' ? 'Automatic update' : displayRole(String(row.role) === 'manager' ? 'manager' : 'user')}</span><strong>{String(row.action).replaceAll('_', ' ')}</strong><span>{String(row.entityType)} {String(row.entityId ?? '')}</span><span>{summarizeChange(row)}</span></summary><pre className="json-view">{customerText(JSON.stringify({ previous: row.previousValue, next: row.newValue }, null, 2))}</pre></details>)}</div><div className="pager"><button className="action ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button><span>{filtered.length} entries · Page {page + 1} of {Math.max(1, Math.ceil(filtered.length / 20))}</span><button className="action ghost" disabled={(page + 1) * 20 >= filtered.length} onClick={() => setPage(page + 1)}>Next</button></div></div>;
}

function ReleasesAdmin() {
  const [releases, setReleases] = useState<ReleaseRecord[]>([]);
  useEffect(() => { void api.releases().then((data) => setReleases(data.releases)); }, []);
  return <div className="release-cards">{releases.map((release) => <article className="release-card" key={release.id}><h2>{release.releaseNumber} Â· {release.version}</h2><p>{formatDate(release.deployedAt)}</p><strong>{release.changeCount} changes</strong><p>{release.summary}</p><ul>{release.changes.map((change) => <li key={change}>{change}</li>)}</ul><small>{release.commitSha}</small></article>)}</div>;
}

function SupportAdmin() {
  const [info, setInfo] = useState<SupportInfo | null>(null);
  useEffect(() => { void api.support().then(setInfo); }, []);
  if (!info) return <p>Loading support details...</p>;
  return <div className="support-card"><div className="atech-logo">A-Tech<br /><span>St. Louis</span></div><h2>Website support provided by {customerText(info.support.providerName)}</h2>{info.support.supportEmail ? <a className="action primary" href={`mailto:${info.support.supportEmail}`}>Email Support</a> : <p className="inline-note">Support email is not configured yet.</p>}{info.support.supportPhone ? <p>{info.support.supportPhone}</p> : <p>A-Tech support phone is not configured yet.</p>}<p>Current site version: <strong>{info.release?.version ?? 'Unknown'} Â· {info.release?.releaseNumber ?? 'Unknown'}</strong></p>{info.support.note && <p className="admin-help">{customerText(info.support.note)}</p>}</div>;
}
function App() {
  const isManagement = useMemo(() => window.location.pathname.startsWith('/manage'), []);
  const isMenu = useMemo(() => window.location.pathname.startsWith('/menu'), []);
  useEffect(() => {
    if (isManagement) {
      document.querySelector('meta[name="robots"]')?.remove();
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex,nofollow';
      document.head.appendChild(meta);
    }
  }, [isManagement]);
  if (isManagement) return <AdminApp />;
  return <>{isMenu ? <MenuPage /> : <PublicSite />}<StagingNotice /></>;
}

createRoot(document.getElementById('root')!).render(<App />);

